/**
 * DestroyedPieceOverlay Component
 *
 * Renders destroyed piece shapes as overlays on the opponent grid.
 * Uses distinct styling (dark red/burnt orange with semi-transparency)
 * to show which pieces have been fully destroyed.
 *
 * Phase 7.9.3.3: Show Destroyed Piece Shapes on Grid
 */

import React from 'react';
import PropTypes from 'prop-types';

/**
 * Convert coordinate string (e.g., "E5") to grid position
 * @param {string} coord - Coordinate in format "A1" to "J10"
 * @returns {{row: number, col: number}} - Zero-indexed row and column
 */
function coordinateToPosition(coord) {
  if (!coord || typeof coord !== 'string' || coord.length < 2) {
    return { row: 0, col: 0 };
  }

  const col = coord.charCodeAt(0) - 'A'.charCodeAt(0); // A=0, B=1, ..., J=9
  const row = parseInt(coord.substring(1), 10) - 1; // 1=0, 2=1, ..., 10=9

  return { row, col };
}

/**
 * DestroyedPieceOverlay Component
 *
 * @param {Array} destroyedPieces - Array of { type, coordinates } objects
 */
function DestroyedPieceOverlay({ destroyedPieces = [] }) {
  // Handle null, undefined, or empty arrays
  if (!destroyedPieces || destroyedPieces.length === 0) {
    return null;
  }

  return (
    <div
      className="absolute inset-0 pointer-events-none"
      data-testid="destroyed-pieces-overlay"
    >
      {destroyedPieces.map((piece, pieceIndex) => (
        <div
          key={pieceIndex}
          className="absolute inset-0"
          data-testid={`destroyed-piece-${pieceIndex}`}
        >
          {(piece.coordinates || []).map((coord) => {
            const { row, col } = coordinateToPosition(coord);

            // Calculate position
            // Grid cell size: 44px (w-11 h-11)
            // Grid starts at margin: 41px left, 30px top (from PlayerGrid fix)
            const cellSize = 44;
            const left = 41 + col * cellSize;
            const top = 30 + row * cellSize;

            return (
              <div
                key={coord}
                data-testid={`destroyed-cell-${pieceIndex}-${coord}`}
                className="absolute"
                style={{
                  left: `${left}px`,
                  top: `${top}px`,
                  width: `${cellSize}px`,
                  height: `${cellSize}px`,
                  backgroundColor: 'rgba(180, 70, 40, 0.5)', // Dark red/burnt orange with 50% opacity
                  border: '1px solid rgba(180, 70, 40, 0.7)',
                  opacity: 0.5, // Semi-transparent overlay
                  zIndex: 1, // Above grid cells but below other overlays
                }}
              />
            );
          })}
        </div>
      ))}
    </div>
  );
}

DestroyedPieceOverlay.propTypes = {
  destroyedPieces: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string.isRequired,
      coordinates: PropTypes.arrayOf(PropTypes.string).isRequired,
    })
  ),
};

export default DestroyedPieceOverlay;
